import React, { useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { api, ApiError } from "../../lib/api"
import { formatBRL, formatDateTime } from "../../lib/format"
import { useMe } from "../../hooks/useMe"
import { AppShell } from "../ui/AppShell"
import { Button } from "../ui/Button"
import { PageLoading, Spinner } from "../ui/Spinner"
import { PagamentoModal } from "./PagamentoModal"
import { useToast } from "../ui/Toast"
import {
  CRITERIO_DE_PRONTO,
  CTA_TRATAMENTO,
  CTA_TRATAMENTO_CONTINUAR,
  GARANTIA,
  MECANISMO,
  PROMESSA,
  TRATAMENTO_LEAD,
  VALIDADE_LINHA,
  pilares,
} from "../../lib/narrativa"
import type { Plan, PlansResponse, PurchaseCreated, PurchaseStatus, Transaction, TransactionsResponse } from "../../types"

/** Divisas de sargento — insígnia da marca. */
function Chevrons({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="square" aria-hidden="true">
      <path d="M4 8.5 12 3.5 20 8.5" />
      <path d="M4 14 12 9 20 14" />
      <path d="M4 19.5 12 14.5 20 19.5" />
    </svg>
  )
}

/** Delay do reveal escalonado como CSS var (utilitário .reveal-up). */
function delay(ms: number): React.CSSProperties {
  return { "--reveal-delay": `${ms}ms` } as React.CSSProperties
}

/**
 * Os quatro pilares da preparação, na ordem do ciclo: nivelar, corrigir,
 * treinar, saber quando parar. O quarto é o critério de pronto por escrito —
 * regra que a pessoa aplica olhando o próprio histórico, não feature futura.
 */
const ICONS: React.ReactNode[] = [
  <>
    <path d="M4 19V5" />
    <path d="M4 19h16" />
    <path d="M8 16v-4M13 16V8M18 16v-6" />
  </>,
  <>
    <path d="M5 4h9l4 4v12H5z" />
    <path d="M8.5 13.5l2 2 4-4.5" />
  </>,
  <>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 8v4.2l2.8 2" />
  </>,
  <>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="3.4" />
  </>,
]
/** Dias restantes a partir dos quais a renovação deixa de ser assunto. */
const RENEWAL_WINDOW_DAYS = 30

const BENEFITS: { icon: React.ReactNode; title: string; body: string }[] = pilares(CRITERIO_DE_PRONTO).map(
  ([title, body], i) => ({ icon: ICONS[i], title, body }),
)

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

export default function Plano() {
  const { me, refetch } = useMe()
  const [data, setData] = useState<PlansResponse | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [error, setError] = useState<string | null>(null)
  const [buyingId, setBuyingId] = useState<string | null>(null)
  const [pixPlan, setPixPlan] = useState<Plan | null>(null)
  const [recoveryPurchaseId, setRecoveryPurchaseId] = useState<string | null>(null)
  const [planosAbertos, setPlanosAbertos] = useState(false)
  const { toast } = useToast()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Retomada de checkout (link do e-mail → /plano?retomar=<id>): reabre o
  // modal com o plano da compra pendente — Pix (Asaas) ou cartão (MP).
  useEffect(() => {
    const retomar = searchParams.get("retomar")
    if (!retomar) return
    api
      .get<PurchaseStatus>(`/api/purchases/${retomar}`)
      .then((p) => {
        if (p.status === "paid") {
          navigate(`/plano/success?purchase_id=${retomar}`)
        } else if (p.status === "pending" && p.plan) {
          setPixPlan(p.plan)
          setRecoveryPurchaseId(retomar)
        }
      })
      .catch(() => {
        /* compra inválida/de outro user: segue na página normal */
      })
      .finally(() => {
        searchParams.delete("retomar")
        setSearchParams(searchParams, { replace: true })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ?origem= diz por onde a pessoa chegou ao preço (resultado, configurador,
  // dashboard, landing, e-mails) — vai junto no preco_visto.
  const origem = searchParams.get("origem")
  useEffect(() => {
    const plansPath = origem ? `/api/plans?origem=${encodeURIComponent(origem)}` : "/api/plans"
    Promise.all([api.get<PlansResponse>(plansPath), api.get<TransactionsResponse>("/api/purchases")])
      .then(([plansRes, txRes]) => {
        setData(plansRes)
        setTransactions(txRes.transactions)
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Erro ao carregar os planos"))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function buy(plan: Plan) {
    setBuyingId(plan.id)
    try {
      const res = await api.post<PurchaseCreated>("/api/purchases", { plan_id: plan.id })
      window.location.href = res.init_point
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Erro ao iniciar a compra. Tente de novo.", "error")
      setBuyingId(null)
    }
  }

  function choose(plan: Plan) {
    if (me && !me.confirmed) {
      toast("Confirme seu e-mail antes de assinar", "info")
      return
    }
    setPixPlan(plan)
  }

  if (error) {
    return (
      <AppShell>
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
          <p className="text-navy-700">{error}</p>
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Tentar de novo
          </Button>
        </div>
      </AppShell>
    )
  }

  if (!data) {
    return (
      <AppShell>
        <PageLoading />
      </AppShell>
    )
  }

  const access = me?.access ?? null

  // Renovar só faz sentido perto do fim. Com plano vigente e folga, a vitrine
  // vira ruído: a pessoa já comprou, e o que ela quer aqui é ver até quando vai
  // e voltar a treinar. O bloco não some de vez — estender antes é legítimo
  // (grant_access! acumula a partir do fim do plano vigente), então fica atrás
  // de um link discreto em vez de ocupar a página.
  // Consequência assumida: quem é do plano Mensal tem sempre menos de 30 dias,
  // logo sempre vê o bloco — o que está certo, um plano de 30 dias está sempre
  // perto de vencer.
  const folgado = access?.active === true && access.days_left >= RENEWAL_WINDOW_DAYS
  const mostrarPlanos = !folgado || planosAbertos

  return (
    <AppShell>
      <div className="flex flex-col gap-6">
        {/* ===== Status do acesso ===== */}
        {access && (
          <section
            aria-label="Seu acesso"
            className={`reveal-up flex flex-col gap-2 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5 ${
              access.active ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"
            }`}
            style={delay(0)}
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-navy-500">Seu acesso</p>
              {access.active && access.expires_at ? (
                <p className="mt-0.5 text-sm text-navy-800">
                  <span className="font-bold text-emerald-700">Tratamento ativo</span> até{" "}
                  <span className="font-semibold">{formatDate(access.expires_at)}</span>
                  {access.days_left > 0 && <span className="text-navy-500"> · {access.days_left} dias</span>}
                </p>
              ) : (
                <p className="mt-0.5 text-sm text-navy-800">
                  <span className="font-bold">Etapas 1 e 2 grátis.</span> Seu diagnóstico gratuito continua de pé: nivelamento e 1 diagnóstico por matéria.
                </p>
              )}
            </div>
            {access.active && !folgado && (
              <p className="text-xs text-navy-500 sm:text-right">
                Renovando antes do fim, os dias novos
                <br className="hidden sm:block" /> começam a contar depois do vencimento.
              </p>
            )}
          </section>
        )}

        {/* ===== Hero ===== */}
        <section
          className="reveal-up relative overflow-hidden rounded-2xl bg-navy-950 px-6 py-9 shadow-xl shadow-navy-900/25 sm:px-9 sm:py-11"
          style={delay(40)}
        >
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-5%,rgba(212,166,42,0.16),transparent_65%)]"
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(-45deg,rgba(212,166,42,0.04)_0px,rgba(212,166,42,0.04)_1px,transparent_1px,transparent_14px)]"
            aria-hidden="true"
          />
          <Chevrons className="pointer-events-none absolute -right-8 -top-8 h-44 w-44 text-white/[0.04]" />

          <div className="relative">
            <p className="font-display text-[11px] font-bold uppercase tracking-[0.24em] text-gold-400 sm:text-xs">
              {folgado ? "Seu tratamento" : "O tratamento"}
            </p>
            <h1 className="mt-2.5 max-w-2xl font-display text-3xl font-bold uppercase leading-[0.98] tracking-tight text-white sm:text-5xl">
              {PROMESSA}
            </h1>
            {folgado ? (
              <>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-navy-200 sm:text-base">
                  Seu tratamento está em dia até{" "}
                  <span className="font-semibold text-white">{access?.expires_at ? formatDate(access.expires_at) : ""}</span>
                  . {MECANISMO} Em qualquer prova da plataforma.
                </p>
                <Link
                  to="/dashboard"
                  className="mt-5 inline-flex items-center justify-center rounded-xl bg-gold-400 px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-navy-950 transition-colors hover:bg-gold-300"
                >
                  {CTA_TRATAMENTO_CONTINUAR}
                </Link>
              </>
            ) : (
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-navy-200 sm:text-base">
                <span className="font-semibold text-white">{TRATAMENTO_LEAD}</span>{" "}
                {MECANISMO} Em qualquer prova da plataforma. Você paga uma vez pelo período —{" "}
                <span className="font-semibold text-gold-300">nada renova sozinho no seu cartão</span>.
              </p>
            )}
          </div>
        </section>

        {/* ===== Planos — escondidos enquanto renovar não é assunto ===== */}
        {folgado && !planosAbertos && (
          <p className="reveal-up text-center text-xs text-navy-400" style={delay(80)}>
            Seu plano está em dia.{" "}
            <button
              type="button"
              onClick={() => setPlanosAbertos(true)}
              className="font-semibold text-navy-600 underline underline-offset-2 hover:text-navy-800"
            >
              Quero estender assim mesmo
            </button>{" "}
            — os dias novos começam a contar depois do vencimento.
          </p>
        )}

        {mostrarPlanos && (
        <section aria-label="Planos" className="reveal-up" style={delay(80)}>
          <div className="mb-3 text-center">
            <h2 className="font-display text-xl font-bold uppercase tracking-wide text-navy-900">
              {access?.active ? "Renovar ou estender" : CTA_TRATAMENTO}
            </h2>
            <p className="mt-1 text-sm text-navy-500">Pagamento único · Pix ou cartão · acesso liberado na hora</p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
            {data.plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative flex flex-col items-center rounded-2xl border p-5 text-center shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                  plan.highlighted
                    ? "border-gold-400 bg-gold-50/70 ring-1 ring-gold-400/40"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className="flex h-4 items-center">
                  {plan.highlighted && (
                    <span className="rounded-full bg-gold-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-navy-950">
                      Melhor valor
                    </span>
                  )}
                </div>
                <h3 className="mt-1 font-display text-lg font-bold uppercase tracking-wide text-navy-900">{plan.name}</h3>
                <p className="mt-2 font-display text-3xl font-extrabold leading-none text-navy-900 tabular-nums sm:text-4xl">
                  {plan.price_formatted}
                </p>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-navy-500">
                  uma vez · etapas 3, 4 e 5
                </p>
                <p className="mt-1 text-sm font-semibold text-gold-600">
                  ou {plan.installments}x de {plan.installment_formatted} no cartão
                </p>
                <p className="mt-2 text-xs text-navy-500">{VALIDADE_LINHA}</p>
                <Button
                  block
                  className="mt-4"
                  variant={plan.highlighted ? "gold" : "secondary"}
                  onClick={() => choose(plan)}
                  disabled={buyingId !== null}
                >
                  {buyingId === plan.id ? <Spinner className="h-4 w-4" /> : access?.active ? "Estender" : "Começar"}
                </Button>
              </div>
            ))}
          </div>

          <p className="mt-3 text-center text-sm text-navy-500">
            Sem renovação automática. <span className="font-semibold text-gold-600">Pagou, usou. Renova se quiser.</span>
            <span className="mt-0.5 block text-xs text-navy-400">
              Pagamento seguro — Pix à vista (Asaas) ou cartão em até 3x (Mercado Pago) · {GARANTIA}
            </span>
          </p>
        </section>
        )}

        {/* ===== O que o plano libera ===== */}
        <section aria-label="O que o plano libera" className="reveal-up" style={delay(160)}>
          <h2 className="mb-3 text-center font-display text-xl font-bold uppercase tracking-wide text-navy-900">
            O que o tratamento faz
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {BENEFITS.map((b) => (
              <div key={b.title} className="flex items-start gap-3.5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gold-50 text-gold-600 ring-1 ring-gold-200">
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    {b.icon}
                  </svg>
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-navy-900">{b.title}</h3>
                  <p className="mt-0.5 text-[13px] leading-relaxed text-navy-500">{b.body}</p>
                </div>
              </div>
            ))}
          </div>
          {!access?.active && (
            <p className="mt-3 text-center text-xs text-navy-400">
              Sem pagar, o diagnóstico: nivelamento + 1 diagnóstico por matéria. O gabarito comentado é sempre liberado.
            </p>
          )}
        </section>

        {pixPlan && (
          <PagamentoModal
            plan={pixPlan}
            recoveryPurchaseId={recoveryPurchaseId}
            onClose={() => {
              setPixPlan(null)
              setRecoveryPurchaseId(null)
            }}
            onPaid={(purchaseId) => {
              setPixPlan(null)
              setRecoveryPurchaseId(null)
              refetch()
              navigate(`/plano/success?purchase_id=${purchaseId}`)
            }}
            onCard={() => {
              const plan = pixPlan
              setPixPlan(null)
              setRecoveryPurchaseId(null)
              void buy(plan)
            }}
          />
        )}

        {transactions.length > 0 && (
          <section aria-label="Seu histórico">
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-navy-400">Seu histórico</h2>
            <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              {transactions.map((t) => (
                <div key={`${t.kind}-${t.id}`} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-navy-900">{t.title}</p>
                    <p className="text-xs text-navy-400">{formatDateTime(t.date)}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className={`text-sm font-semibold tabular-nums ${t.kind === "system" ? "text-gold-600" : "text-navy-900"}`}>
                      {t.days != null ? `+${t.days} dias` : "Acesso"}
                    </p>
                    <p className="text-xs text-navy-400">{t.amount_cents != null ? formatBRL(t.amount_cents) : "Cortesia"}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </AppShell>
  )
}
